<!-- src/components/TicketToggle.svelte -->
<script>
	import { addToCart, showErrorToast } from '$lib/helpers';
	import { onMount, onDestroy } from 'svelte';
	import PlusIcon from '$lib/plussml.png';
	import MinusIcon from '$lib/minussml.png';
	import { logError } from '$lib/helpers/errors';
	import { page } from '$app/stores';
	import { browser } from '$app/environment';
	import { prizes, subscribeToPrizes } from '$stores/prizes';
	import { goto } from '$app/navigation';
	import { FacebookPixel, fb } from '@beyonk/svelte-facebook-pixel';
	import { favesStore } from '$stores/faves';
	import posthog from 'posthog-js';
	import { MAX_LIMIT, LIMITS_BY_PID, ticketsStore } from '$stores/tickets';
	import { get } from 'svelte/store';
	import ExpressCheckout from './ExpressCheckout.svelte';
	import { cartContents } from '$stores/cart';
	import { userStore } from '$stores/user';
	import { ordersCount, fetchOrdersCount } from '$stores/ordersCount';
	import { trackAddedToCart, trackAddedToCartFirstOrder } from '$lib/klaviyo/events';
	import { isActiveSubscriber } from '$stores/subscription';

	export let qty = 5;
	export let data;
	export let isSingle = false;
	export let isListing = false;
	export let hasTicketToggle = true;
	export let pid;
	export let isUpsell = false;
	export let isHighlight = false;

	let wiggle = false;
	let maxStock = 50;
	let availStock = 0;
	let parentClasses = '';
	let prize = null;

	$: MAX_TICKET_CAP = LIMITS_BY_PID?.[pid] ?? MAX_LIMIT; // some comment here.

	/* ---------- Express-Checkout gate ---------- */
	let showExpress = false;

	function expressAllowed() {
		if (!browser) return false;
		const FLAG = 'express_checkout_flag';
		if (!posthog.isFeatureEnabled?.(FLAG)) return false;

		const payload = posthog.getFeatureFlagPayload?.(FLAG);
		if (!payload) return true; // everyone

		let list;
		try {
			list = JSON.parse(payload);
		} catch (e) {
			list = String(payload).split(',');
		}

		const email = (get(userStore).email || '').toLowerCase();
		const id = (posthog.get_distinct_id?.() || '').toLowerCase();
		return list.map((s) => s.trim().toLowerCase()).includes(email) || list.includes(id);
	}

	/* reactive – runs when cart changes ----------------------------- */
	$: {
		const qtyInCart = $cartContents.reduce(
			(t, it) => t + ((it.id ?? it.item?.id) === pid ? (it.qty ?? it.quantity ?? 0) : 0),
			0
		);
		showExpress = browser && expressAllowed() && qtyInCart > 0;
	}

	// Determine if the prize is a subscriber-only prize (stable across hydration)
	// Prefer a boolean if it exists; otherwise fall back to category string.
	let showSubscribeCTA = false; // latched once true
	$: {
		const src = prize ?? data;
		const cat = (src?.category ?? '').toString().toLowerCase();
		const requiresSub =
			src?.subscriber_only === true ||
			src?.subscriberOnly === true ||
			src?.subscribe_only === true ||
			cat === 'subscriber';

		// Latch: once we know it’s subscriber-only, keep it that way for this render lifecycle
		if (requiresSub) showSubscribeCTA = true;
	}

	// Reactive statement to find the prize from the store
	$: if (browser && $prizes.length > 0 && pid) {
		prize = $prizes.find((p) => p.id === pid);
		if (!prize) {
			console.warn(`Prize with id ${pid} not found`);
		}
	}

	$: prize = prize || data;

	// Reactive statement to update available stock when prize changes
	$: if (browser) {
		availStock = prize ? prize.stock : 0;
	}

	// Reactive statement to adjust maxStock based on data.price and availStock
	$: if (browser) {
		try {
			if (availStock === null || availStock === undefined) {
				availStock = 0;
				console.warn(`Stock check failed for pid ${pid}. Defaulting availStock to 0.`);
			}

			// Adjust maxStock based on data.price
			if (data.price >= 3) {
				maxStock = Math.min(50, availStock);
			} else if (data.price >= 2) {
				maxStock = Math.min(50, availStock);
			} else if (data.price >= 1) {
				maxStock = Math.min(50, availStock);
			} else if (data.price >= 0.2) {
				maxStock = Math.min(200, availStock);
			} else if (data.price > 0) {
				maxStock = Math.min(1000, availStock);
			} else if (data.price === 0) {
				maxStock = posthog?.isFeatureEnabled('double-free-tickets') ? 2 : 1;
			}

			// Adjust for upsell logic
			if (isUpsell) {
				maxStock = Math.min(5, availStock);
			}

			// Ensure maxStock is at least 1
			maxStock = Math.max(1, maxStock);
		} catch (err) {
			console.error('Stock adjustment failed:', err);
			logError(err, 'TicketToggle: Stock adjustment failed');
			// Set fallback stock values to ensure rendering happens
			availStock = 0;
			maxStock = 0;
			qty = 1;
		}
	}

	/**
	 * Sets the initial quantity based on data.price and availStock
	 */
	function setInitialQty() {
		if (data.price >= 3) {
			qty = Math.max(1, Math.min(8, availStock));
		} else if (data.price >= 2) {
			qty = Math.max(1, Math.min(22, availStock));
		} else if (data.price >= 1) {
			qty = Math.max(1, Math.min(31, availStock));
		} else if (data.price >= 0.2) {
			qty = Math.max(1, Math.min(59, availStock));
		} else if (data.price > 0) {
			qty = Math.max(1, Math.min(200, availStock));
		} else if (data.price === 0) {
			qty = maxStock;
		}

		// Adjust for upsell logic
		if (isUpsell) {
			qty = Math.max(1, Math.min(5, availStock));
		}

		// Ensure the quantity doesn't exceed available stock
		qty = Math.max(1, Math.min(qty, availStock));
	}

	/**
	 * Reactive statement to adjust qty and maxStock based on ticketsStore and current pid
	 */
	$: {
		const cappedTickets = $ticketsStore;
		// console.log('Current PID:', pid);
		// console.log('Capped Tickets:', cappedTickets);

		if (cappedTickets && cappedTickets[pid] !== undefined) {
			const purchasedCount = cappedTickets[pid];
			const remaining = MAX_TICKET_CAP - purchasedCount;

			if (remaining <= 0) {
				maxStock = 0;
				qty = 0;
				console.log(`Max ticket cap reached for pid ${pid}`);
			} else {
				maxStock = Math.min(maxStock, remaining);
				qty = Math.min(qty, remaining);
			}
		}
	}

	/**
	 * Function to add the item to the cart
	 */
	async function addItem(item) {
		if (!browser) return;
		// console.log({
		//     item,
		//     availStock,
		//     qty,
		// });
		try {
			if (availStock < qty) {
				showErrorToast('Insufficient stock', `Only ${availStock} tickets left`);
				return;
			}

			const objectToAdd = {
				item,
				qty,
				cost: (data.price * qty).toFixed(2),
				pid
			};

			trackAnalytics(item); // Track the action in analytics

			addToCart(objectToAdd); // Add the item to the cart

			wiggleEffect(); // Show wiggle animation
		} catch (error) {
			console.error('Error adding item to cart:', error);
			showErrorToast('Error', 'Failed to add item to cart');
		}
	}

	/**
	 * Function for quick adds (e.g., 10, 20, 50 tickets)
	 */
	async function handleQuickAdds(quantity) {
		if (!browser) return;

		try {
			if (availStock < quantity) {
				showErrorToast('Insufficient stock', `Only ${availStock} tickets left`);
				return;
			}

			const objectToAdd = {
				item: data,
				qty: quantity,
				cost: (data.price * quantity).toFixed(2),
				pid
			};

			addToCart(objectToAdd);

			trackAnalytics(data, 'quick_add');
			wiggleEffect();

			// Update ticketsStore after quick add
			if ($ticketsStore[pid] !== undefined) {
				ticketsStore.addCount(pid, quantity);
				console.log(`Quick added ${quantity} tickets for pid ${pid}`);
			}
		} catch (error) {
			console.error('Error with quick add:', error);
			showErrorToast('Error', 'Failed to add tickets to cart');
		}
	}

	/**
	 * Function to track analytics
	 */
	function trackAnalytics(item, location) {
		if (!browser) return;

		if (!location) {
			location = isSingle ? 'single' : isListing ? 'listing' : isUpsell ? 'upsell' : 'unknown';
		}

		// Posthog tracking
		posthog.capture('ticket_added', {
			currency: 'GBP',
			value: (data.price * qty).toFixed(2),
			item_id: data?.name?.replace(' ', '_'),
			item_name: data?.name,
			price: data.price,
			quantity: qty,
			location: location,
			highlighted: isHighlight
		});

		// Facebook Pixel tracking
		fb?.track('AddToCart', {
			currency: 'GBP',
			value: (data.price * qty).toFixed(2),
			contents: data,
			content_ids: data?.id,
			content_name: data?.name,
			content_type: 'product'
		});

		// Klaviyo tracking
		const klaviyoPayload = {
			value: (data.price * qty).toFixed(2),
			properties: {
				AddedItemProductName: item.name,
				AddedItemProductID: item.id,
				SKU: item.id,
				Categories: [item.category],
				ImageURL: item.featured_image,
				URL: `https://www.hotdraw.co.uk/prizes/${item.id}`,
				Price: item.price,
				AddedItemQuantity: qty,
				CustomerType: $ordersCount === 0 ? 'new' : 'existing'
			}
		};
		trackAddedToCart(klaviyoPayload);

		if ($ordersCount === 0) {
			trackAddedToCartFirstOrder(klaviyoPayload);
		}
	}

	/**
	 * Animation for the wiggle effect when adding to cart
	 */
	function wiggleEffect() {
		if (!browser) return;

		wiggle = true;
		setTimeout(() => (wiggle = false), 1000);
	}

	/**
	 * Adjust quantity with bounds
	 */
	function adjustQuantity(delta) {
		// console.log({delta, qty, maxStock});
		qty = Math.max(1, Math.min(qty + delta, maxStock)); // Keep qty between 1 and maxStock
	}

	onMount(() => {
		if (!browser) return;

		// if ($ordersCount === null) {
		// const email = get(userStore).email;
		// if (email) fetchOrdersCount(email);
		// }

		pid = pid || $page.params.pid || data?.id;

		// Subscribe to the prizes store
		const unsubscribe = subscribeToPrizes();

		// Initial setup for parent classes
		parentClasses = [
			'ticket-toggle',
			isListing && 'listing-ticket--toggle',
			isUpsell && 'upsell-ticket-toggle',
			isHighlight && 'highlight-ticket-toggle'
		]
			.filter(Boolean)
			.join(' ');

		// Initial setup done via reactive blocks
		setInitialQty();

		// Return the unsubscribe function to clean up on destroy
		return () => {
			unsubscribe();
		};
	});

	onDestroy(() => {
		// Any additional cleanup if necessary
	});
</script>

<!-- Template HTML -->
{#if browser}
	<FacebookPixel />
{/if}

<div class={parentClasses}>
	{#if hasTicketToggle}
		{#if !showSubscribeCTA}
			{#if $ticketsStore[pid] === MAX_TICKET_CAP}
				<p class="max-tickets-title">{MAX_TICKET_CAP} Tickets Max</p>
				<p class="max-tickets-copy">You have {MAX_TICKET_CAP} of {MAX_TICKET_CAP} tickets</p>
			{:else}
				<div class="flex ticket-toggle--wrap">
					<div class="flex qty-btns">
						<button
							class="qty-btn"
							on:click={() => adjustQuantity(-1)}
							disabled={qty <= 1}
							aria-label="Decrease quantity"
						>
							<img src={MinusIcon} alt="Decrease quantity" />
						</button>
						<div class="total-ticket" data-max={maxStock}>{qty}</div>
						<button
							class="qty-btn"
							on:click={() => adjustQuantity(1)}
							disabled={qty >= maxStock}
							aria-label="Increase quantity"
						>
							<img src={PlusIcon} alt="Increase quantity" />
						</button>
					</div>

					<div class="flex cta-wrap {isUpsell ? 'upsell-tickets' : ''}">
						<button
							class="btn"
							on:click|preventDefault={() => addItem(data)}
							aria-label="Add tickets to cart"
						>
							<img
								width="22"
								class={wiggle ? 'shakeitlikeits-hot' : ''}
								src="/images/ticket-white.png"
								alt="Ticket collection"
							/>
							<span>Add {qty > 1 ? 'tickets' : 'ticket'}</span>
						</button>
					</div>
				</div>

				<!-- Quick add buttons for 10, 20, 50 tickets -->
				{#if maxStock > 10}
					<div class="block-tickets">
						<div class="flex">
							{#if maxStock >= 10}
								<button on:click={() => handleQuickAdds(10)} class="btn">
									<span>10</span>
								</button>
							{/if}

							{#if maxStock >= 20}
								<button on:click={() => handleQuickAdds(20)} class="btn">
									<span>20</span>
								</button>
							{/if}

							{#if maxStock >= 50}
								<button on:click={() => handleQuickAdds(50)} class="btn">
									<span>50</span>
								</button>
							{/if}
						</div>
					</div>
				{/if}
				{#if showExpress}
					<div class="express-btn">
						<ExpressCheckout instanceId={pid} />
					</div>
				{/if}
			{/if}
		{:else}
			<button
				class="btn"
				on:click={() => {
					goto('/subscribe?utm_source=hotdraw&utm_medium=prize_page&utm_campaign=subscribe_cta');
					posthog?.capture('subscribe_cta_clicked', { prize_id: data.id });
				}}
			>
				Subscribe & Enter
			</button>
		{/if}
	{:else}
		<a class="info-link" target="_self" href={`/prizes/${data.id}`}> i </a>
	{/if}
</div>

<style lang="scss">
	.express-btn {
		margin-top: 5px;
	}

	.info-link {
		width: 26px;
		height: 26px;
		border: 2px solid #fe1100;
		text-align: center;
		padding-top: 2px;
		border-radius: 13px;
		font-weight: 700;
	}

	.tt-single {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		background-color: #fff;
		padding: 12px 20px;
		z-index: 999;
		box-shadow: 0px 0px 16px -3px rgba(107, 107, 107, 1);

		@media only screen and (min-width: 479px) {
			position: static;
			box-shadow: none;
		}

		.qty-btns .qty-btn {
			width: 48px;
		}
	}

	.ticket-toggle--wrap {
		flex-direction: row;
		justify-content: space-around;
	}

	.qty-btns {
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: space-evenly;
		align-items: center;

		button.qty-btn {
			appearance: none;
			background: transparent;
			border: none;
			padding: 4px;
			width: 40px;
			position: relative;

			&::after {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				width: 100%;
				height: 100%;
				background-color: transparent;
				z-index: 2;
			}

			img {
				max-width: 100%;
				height: auto;
				transition: ease all 0.2s;
				pointer-events: none;
				user-select: none;

				&:hover {
					transform: scale(0.98);
				}
			}
		}

		.total-ticket {
			width: 40px;
			text-align: center;
			font-weight: 600;
			font-size: 20px;
			user-select: none;
		}
	}

	.cta-wrap {
		align-items: center;
		justify-content: space-between;
		flex-basis: 55%;

		button {
			display: flex;
			align-items: center;
			justify-content: center;
			/* min-width: 200px; */

			span {
				min-width: 100px;
			}
		}

		@media only screen and (max-width: 479px) {
			button {
				min-width: auto;
			}
		}

		&.upsell-tickets {
			button.btn {
				padding: 0.35rem;
				font-size: 14px;
			}
		}
	}

	.listing-ticket--toggle {
		.ticket-toggle--wrap {
			flex-direction: column;

			.qty-btns {
				margin-bottom: 7px;
			}
		}
	}

	.highlight-ticket-toggle {
		.cta-wrap button.btn {
			background-color: goldenrod;
		}
	}

	.block-tickets {
		margin-top: 5px;
		.flex {
			justify-content: space-between;

			button {
				padding: 10px 0.15rem;
				margin: 0 3px;
				span {
					display: block;
				}

				&:first-of-type {
					margin-left: 0;
				}
				&:last-of-type {
					margin-right: 0;
				}
			}
		}
	}

	.max-tickets-title,
	.max-tickets-copy {
		text-align: center;
		font-weight: 600;
		margin-bottom: 0;
	}
	.max-tickets-title {
		font-size: 1.1rem;
	}
</style>
